import { useEffect, useMemo, useState } from "react";
import { searchLensCatalog } from "../lens-library.js";
import { useLensApp } from "../useLensApp.jsx";
import LensCardGrid from "./LensCardGrid.jsx";

export default function LensLibraryPage() {
  const {
    favorites, favoriteIds, recentLenses, activeLenses, busyLensId, favoriteError, actions,
  } = useLensApp();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const initialView = ["browse", "favorites", "recent"].includes(params.get("view")) ? params.get("view") : "browse";
  const [view, setView] = useState(initialView);
  const [query, setQuery] = useState(params.get("q") || "Quin");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (view !== "browse") return undefined;
    const value = query.trim();
    if (value.length < 3) { setResults([]); setError(""); return undefined; }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      setLoading(true);
      setError("");
      searchLensCatalog({ query: value, pageSize: 36, signal: controller.signal })
        .then((result) => setResults(result.lenses))
        .catch((nextError) => { if (nextError.name !== "AbortError") setError(nextError.message); })
        .finally(() => setLoading(false));
    }, 220);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, view]);

  const lenses = view === "favorites" ? favorites : (view === "recent" ? recentLenses : results);
  const empty = view === "favorites"
    ? "No favorites yet. Browse and tap a star to add one."
    : (view === "recent" ? "No recently used lenses yet." : (query.trim().length < 3 ? "Enter at least three characters." : "No matching lenses."));
  const activeLensId = String(activeLenses[0]?.lensId || "");

  return (
    <main className="lens-library-page">
      <header className="library-topbar">
        <a className="back-link" href="/ui/" aria-label="Back to Lens Control">←</a>
        <div>
          <h1>Lens Library</h1>
          <div className="subtle">Search, star, and load a lens</div>
        </div>
        <a className="library-done" href="/ui/">Done</a>
      </header>

      <div className="library-toolbar">
        <nav className="library-tabs" aria-label="Lens views">
          {["browse", "favorites", "recent"].map((name) => (
            <button key={name} className={view === name ? "selected" : ""} onClick={() => setView(name)}>
              {name === "recent" ? "Recent" : `${name[0].toUpperCase()}${name.slice(1)}`}
              {name === "favorites" && favorites.length ? ` ${favorites.length}` : ""}
            </button>
          ))}
        </nav>
        {view === "browse" && (
          <input
            className="library-search"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search lenses or creators"
            autoFocus
          />
        )}
      </div>

      <div className="library-content">
        {(error || favoriteError) && <div className="library-error">{error || favoriteError}</div>}
        {loading && view === "browse" && <div className="library-loading">Searching…</div>}
        <LensCardGrid
          lenses={lenses}
          favoriteIds={favoriteIds}
          activeLensId={activeLensId}
          busyLensId={busyLensId}
          onApply={actions.toggleCatalogLens}
          onFavorite={actions.toggleFavorite}
          empty={empty}
        />
      </div>
    </main>
  );
}
